import { gql } from "@apollo/client";
import { client } from "../apolloClient";

/**
 * useHolidayApi custom hook for holiday API operations.
 * - Provides query operations for holidays using Apollo Client and GraphQL.
 * - Handles cache updates for holiday queries.
 * - Exposes helpers for reading/writing holidays in Apollo cache.
 * - Returns methods for fetching holidays.
 * - Ensures UI stays in sync with backend by updating Apollo cache after operations.
 */

// GraphQL Queries
const HOLIDAY_QUERIES = {
  // Query to fetch all holidays
  GET_HOLIDAYS: gql`
    query GetHolidays {
      GetHolidays {
        holiday_id
        holiday_name
        holiday_date
        holiday_description
        is_active
        created_on
        updated_on
      }
    }
  `,
};

// Cache operations for holidays
const cacheOperations = {
  /**
   * Reads the holidays array from Apollo Client cache.
   * @returns {Array} Array of holiday objects or empty array if not found.
   */
  readHolidays: () => {
    try {
      const { GetHolidays = [] } =
        client.readQuery({ query: HOLIDAY_QUERIES.GET_HOLIDAYS }) || {};
      return GetHolidays;
    } catch {
      return [];
    }
  },
  /**
   * Writes the provided holidays array to Apollo Client cache.
   * @param {Array} holidays - Array of holiday objects.
   */
  writeHolidays: (holidays) => {
    client.writeQuery({
      query: HOLIDAY_QUERIES.GET_HOLIDAYS,
      data: { GetHolidays: holidays },
    });
  },
  /**
   * Updates a holiday in the Apollo Client cache.
   * @param {Object} updatedHoliday - The updated holiday object.
   */
  updateHolidayInCache: (updatedHoliday) => {
    const existingHolidays = cacheOperations.readHolidays();
    const updatedHolidays = existingHolidays.map((holiday) =>
      holiday.holiday_id === updatedHoliday.holiday_id
        ? updatedHoliday
        : holiday
    );
    cacheOperations.writeHolidays(updatedHolidays);
  },
};

export const useHolidayApi = () => {
  /**
   * Fetches all holidays from the backend.
   * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
   */
  // Fetch holidays from the bakend
  const getHolidays = async () => {
    try {
      const { data } = await client.query({
        query: HOLIDAY_QUERIES.GET_HOLIDAYS,
        fetchPolicy: "network-only",
      });

      if (!data || !data.GetHolidays) {
        throw new Error("No data received from getHolidays");
      }

      return { success: true, data: data.GetHolidays };
    } catch (error) {
      console.error("Error in getHolidays:", error);
      return { success: false, error: error.message };
    }
  };

  return {
    getHolidays,
    cacheOperations,
  };
};
